import axiosInstance from '../utils/axiosConfig';

/**
 * Paper Pricing (ledger row 132): the hedging desk's daily price board, its
 * benchmark list and paper instruments, and the fixed-versus-floating tasks.
 *
 * Every rule lives on the server -- who may open the module (IsHedgingDesk),
 * what a price file contains, whether a benchmark may be switched off, when a
 * preview has gone stale. This file only carries requests and answers. Errors
 * are passed on untouched, so a screen can show the server's own sentence
 * (utils/errorMessages.extractApiError) instead of a guess.
 */
const BASE = '/api/market-pricing/';
const TASKS = '/api/fuel-enquiries/hedging-tasks/';

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

// A filter with no value is left out of the request, so the server applies its
// own default ("the latest day with a price", "every benchmark") rather than
// being asked about an empty string.
const withoutBlanks = (params) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
);

/* ---- Benchmarks ------------------------------------------------------- */

/** `active`: 'true' | 'false' | 'all'. Answers { results, counts: { active, inactive } }. */
export const fetchBenchmarks = async ({ search = '', active = 'all' } = {}) => {
    const { data } = await axiosInstance.get(`${BASE}benchmarks/`, {
        params: withoutBlanks({ search, active }),
    });
    return data;
};

export const createBenchmark = async (payload) => {
    const { data } = await axiosInstance.post(`${BASE}benchmarks/`, payload);
    return data;
};

/** The code is never sent: it is how price files find a benchmark, so it cannot change. */
export const updateBenchmark = async (id, payload) => {
    const { data } = await axiosInstance.patch(`${BASE}benchmarks/${id}/`, payload);
    return data;
};

export const deactivateBenchmark = async (id) => {
    const { data } = await axiosInstance.post(`${BASE}benchmarks/${id}/deactivate/`);
    return data;
};

export const activateBenchmark = async (id) => {
    const { data } = await axiosInstance.post(`${BASE}benchmarks/${id}/activate/`);
    return data;
};

/* ---- Paper instruments ------------------------------------------------ */

export const fetchInstruments = async ({ search = '', active = 'all' } = {}) => {
    const { data } = await axiosInstance.get(`${BASE}instruments/`, {
        params: withoutBlanks({ search, active }),
    });
    return data;
};

export const createInstrument = async (payload) => {
    const { data } = await axiosInstance.post(`${BASE}instruments/`, payload);
    return data;
};

export const updateInstrument = async (id, payload) => {
    const { data } = await axiosInstance.patch(`${BASE}instruments/${id}/`, payload);
    return data;
};

export const deactivateInstrument = async (id) => {
    const { data } = await axiosInstance.post(`${BASE}instruments/${id}/deactivate/`);
    return data;
};

export const activateInstrument = async (id) => {
    const { data } = await axiosInstance.post(`${BASE}instruments/${id}/activate/`);
    return data;
};

/* ---- Prices ----------------------------------------------------------- */

/** One pricing day. An empty `date` asks for the latest day that has any price. */
export const fetchDailyPrices = async ({ date, region, product, unit, bate } = {}) => {
    const { data } = await axiosInstance.get(`${BASE}prices/daily/`, {
        params: withoutBlanks({ date, region, product, unit, bate }),
    });
    return data;
};

/** Without `from`/`to` the server answers the last 365 days up to the latest price. */
export const fetchPriceHistory = async ({ benchmarkId, bate, from, to }) => {
    const { data } = await axiosInstance.get(`${BASE}prices/history/`, {
        params: withoutBlanks({ benchmark: benchmarkId, bate, from, to }),
    });
    return data;
};

/** Stores one price typed by hand; the server marks it Manual. */
export const enterManualPrice = async ({ benchmarkId, assessedOn, bate, value }) => {
    const { data } = await axiosInstance.post(`${BASE}prices/manual/`, {
        benchmark_id: benchmarkId,
        assessed_on: assessedOn,
        bate,
        value,
    });
    return data;
};

/** Manual prices only; the server refuses a confirmed one in words. */
export const updateManualPrice = async (id, value) => {
    const { data } = await axiosInstance.patch(`${BASE}prices/${id}/`, { value });
    return data;
};

export const deleteManualPrice = async (id) => {
    await axiosInstance.delete(`${BASE}prices/${id}/`);
};

/* ---- Price file imports ----------------------------------------------- */

/** Reads the file and reports what WOULD change. Writes nothing. */
export const previewPriceImport = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await axiosInstance.post(`${BASE}imports/preview/`, form, MULTIPART);
    return data;
};

/**
 * Applies the file the preview described. The fingerprint is what lets the
 * server refuse (409) when the file or the board changed since that preview,
 * so confirming without one is refused here rather than sent.
 */
export const confirmPriceImport = async (file, fingerprint) => {
    if (!fingerprint) {
        throw new Error('Preview the file before confirming it.');
    }
    const form = new FormData();
    form.append('file', file);
    form.append('fingerprint', fingerprint);
    const { data } = await axiosInstance.post(`${BASE}imports/confirm/`, form, MULTIPART);
    return data;
};

/* ---- Hedging tasks ---------------------------------------------------- */

/** `status`: 'open' | 'done' | 'all'. Answers { results, counts: { open, done } }. */
export const fetchHedgingTasks = async (status) => {
    const { data } = await axiosInstance.get(TASKS, { params: { status } });
    return data;
};

export const markHedgingTaskDone = async (id, note) => {
    const { data } = await axiosInstance.post(`${TASKS}${id}/done/`, { note });
    return data;
};

export const reopenHedgingTask = async (id) => {
    const { data } = await axiosInstance.post(`${TASKS}${id}/reopen/`);
    return data;
};
